import { z } from 'zod'
import { UserSchema } from './user'
import { SubscriberReferenceSchema } from './subscriber'
import { ClientReferenceSchema } from './client'

// Role type enum
export const RoleTypeSchema = z.enum([
  'super-admin',
  'platform-admin',
  'agency-admin',
  'agency-user',
])

export type RoleType = z.infer<typeof RoleTypeSchema>

// User Role schema
export const UserRoleSchema = z.object({
  id: z.number(),
  role: RoleTypeSchema,
  subscriber_id: z.number().nullable(),
  subscriber_name: z.string().nullable().optional(),
  client_id: z.number().nullable().optional(),
  client_name: z.string().nullable().optional(),
  context_name: z.string(),
  context_description: z.string().nullable().optional(),
  is_active: z.boolean(),
  expires_at: z.string().nullable(),
  permissions: z.any().nullable().optional(),

  subscriber: SubscriberReferenceSchema.nullable().optional(),
  client: ClientReferenceSchema.nullable().optional(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
  created_by: UserSchema.optional(),
  updated_by: UserSchema.optional(),
})

export type UserRole = z.infer<typeof UserRoleSchema>

// User Context schema
export const UserContextSchema = z.object({
  id: z.number(),
  name: z.string(),
  role: RoleTypeSchema,
  subscriber_id: z.number().nullable(),
  subscriber_name: z.string().nullable().optional(),
  client_id: z.number().nullable(),
  client_name: z.string().nullable().optional(),
  is_current: z.boolean(),
})

export type UserContext = z.infer<typeof UserContextSchema>

// User Permissions schema
export const UserPermissionsSchema = z.object({
  role: RoleTypeSchema.nullable(),
  subscriber_id: z.number().nullable(),
  client_id: z.number().nullable(),
  platform_subscriber_context: z.number().nullable().optional(),
  is_super_admin: z.boolean(),
  is_platform_admin: z.boolean(),
  is_agency_admin: z.boolean(),
  is_agency_user: z.boolean(),
  can_manage_clients: z.boolean(),
  can_create_clients: z.boolean(),
  can_invite_users: z.boolean(),
  can_view_billing: z.boolean(),
  has_all_clients_access: z.boolean(),
  accessible_client_ids: z.array(z.union([z.number(), z.string()])),
  custom_permissions: z.array(z.string()),
})

export type UserPermissions = z.infer<typeof UserPermissionsSchema>

// Subscriber picker item (for platform admins)
export const SubscriberPickerItemSchema = z.object({
  id: z.number(),
  name: z.string(),
  short_name: z.string().nullable(),
  icon_logo_url: z.string().nullable().optional(),
})

export type SubscriberPickerItem = z.infer<typeof SubscriberPickerItemSchema>

// Extended User schema (with role data)
export const UserAuthSchema = UserSchema.extend({
  current_role: UserRoleSchema.optional().nullable(),
  available_contexts: z.array(UserContextSchema).optional(),
  permissions: UserPermissionsSchema.optional(),
  has_multiple_roles: z.boolean().optional(),
  platform_subscriber_context: z.number().nullable().optional(),
  available_subscribers: z.array(SubscriberPickerItemSchema).nullable().optional(),
})

export type UserAuth = z.infer<typeof UserAuthSchema>

// API Request/Response schemas
export const ContextSwitchRequestSchema = z.object({
  subscriber_id: z.number().nullable(),
  client_id: z.number().nullable().optional(),
})

export type ContextSwitchRequest = z.infer<typeof ContextSwitchRequestSchema>

export const ContextSwitchResponseSchema = z.object({
  message: z.string(),
  current_role: UserRoleSchema,
  user: UserAuthSchema,
})

export type ContextSwitchResponse = z.infer<typeof ContextSwitchResponseSchema>

// API Response wrapper
export const ContextSwitchApiResponseSchema = z.object({
  data: ContextSwitchResponseSchema,
})

export type ContextSwitchApiResponse = z.infer<
  typeof ContextSwitchApiResponseSchema
>
